import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import QRCode from 'react-qr-code';
import '../styles/ticket.css';

const TicketDisplay = () => {
  const { language } = useLanguage();
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketData, setTicketData] = useState(null);

  // Debug: Log the current language
  console.log('🎯 TicketDisplay - Current language:', language);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await fetch(`/api/get-ticket?ticketId=${ticketId}`);
        
        if (!response.ok) {
          throw new Error(t('ticket.display.ticketNotFound', language));
        }

        const data = await response.json();
        console.log('🎫 Ticket data received:', data);
        setTicketData(data);
      } catch (err) {
        console.error('Error fetching ticket:', err);
        setError(t('ticket.display.ticketNotFound', language));
      } finally {
        setLoading(false);
      }
    };

    if (ticketId) {
      fetchTicket();
    }
  }, [ticketId, language]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="ticket-page">
        <Header />
        <div className="ticket-container">
          <p>{t('ticket.display.loadingTicket', language)}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !ticketData) {
    return (
      <div className="ticket-page">
        <Header />
        <div className="ticket-container">
          <h1>{t('ticket.display.error', language)}</h1>
          <p>{error || t('ticket.display.ticketNotFound', language)}</p>
          <button onClick={() => navigate('/events')} className="ticket-button">
            {t('ticket.display.backToEvents', language)}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Debug: Log ticket data structure
  console.log('🎫 Rendering ticket with data:', ticketData);
  console.log('🎫 Event data:', ticketData?.event);

  return (
    <div className="ticket-page">
      <Header />
      
      <section className="ticket-hero">
        <div className="ticket-hero-content">
          <h1 className="ticket-hero-title">
            {t('ticket.display.yourTicket', language)}
          </h1>
          <p className="ticket-hero-subtitle">{ticketData?.event?.title || 'Loading...'}</p>
        </div>
      </section>

      <section className="ticket-content-section">
        <div className="ticket-content">
          <div className="ticket-card">
            <div className="ticket-header">
              <h2>{ticketData.event.title}</h2>
              <span className={`ticket-status ${ticketData.isActive ? 'active' : 'inactive'}`}>
                {ticketData.isActive 
                  ? t('ticket.display.active', language)
                  : ticketData.isExpired
                    ? t('ticket.display.expired', language)
                    : t('ticket.display.cancelled', language)
                }
              </span>
            </div>

            {ticketData.isActive && (
              <div className="ticket-qr-section">
                <QRCode 
                  value={`${window.location.origin}/ticket/${ticketData.ticketId}`}
                  size={250}
                  level="H"
                />
                <p className="ticket-qr-instruction">
                  {t('ticket.display.showQRAtEntry', language)}
                </p>
              </div>
            )}

            <div className="ticket-details">
              <div className="ticket-detail-row">
                <span className="ticket-label">
                  {t('ticket.display.ticketId', language)}:
                </span>
                <span className="ticket-value">{ticketData.ticketId}</span>
              </div>

              <div className="ticket-detail-row">
                <span className="ticket-label">
                  {t('ticket.display.name', language)}:
                </span>
                <span className="ticket-value">{ticketData.attendeeName}</span>
              </div>

              <div className="ticket-detail-row">
                <span className="ticket-label">{t('ticket.display.email', language)}:</span>
                <span className="ticket-value">{ticketData.buyerEmail}</span>
              </div>

              <div className="ticket-detail-row">
                <span className="ticket-label">
                  {t('ticket.display.eventDate', language)}:
                </span>
                <span className="ticket-value">{formatDate(ticketData.event.date)}</span>
              </div>

              <div className="ticket-detail-row">
                <span className="ticket-label">
                  {t('ticket.display.price', language)}:
                </span>
                <span className="ticket-value">{ticketData.price} {ticketData.currency}</span>
              </div>

              <div className="ticket-detail-row">
                <span className="ticket-label">
                  {t('ticket.display.purchaseDate', language)}:
                </span>
                <span className="ticket-value">{formatDate(ticketData.purchaseDate)}</span>
              </div>

              {ticketData.isExpired && (
                <div className="ticket-expired-notice">
                  <p>
                    {t('ticket.display.expiredAfterEvent', language)}
                  </p>
                </div>
              )}
            </div>

            <div className="ticket-actions">
              {ticketData.isActive && (
                <button 
                  onClick={() => window.print()} 
                  className="ticket-button ticket-button-primary"
                >
                  {t('ticket.display.printTicket', language)}
                </button>
              )}
              <button 
                onClick={() => navigate('/events')} 
                className="ticket-button ticket-button-secondary"
              >
                {t('ticket.display.browseEvents', language)}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TicketDisplay;
